export type VenueType =
  | 'restaurant'
  | 'cafe'
  | 'bar'
  | 'lounge'
  | 'hotel'
  | 'food_court'
  | 'ghost_kitchen'
  | 'other';

export type ServiceMode = 'waiter' | 'self_service' | 'hybrid';

export type Emirate =
  | 'Dubai'
  | 'Abu Dhabi'
  | 'Sharjah'
  | 'Ajman'
  | 'Umm Al Quwain'
  | 'Ras Al Khaimah'
  | 'Fujairah';

export const EMIRATES: Emirate[] = [
  'Dubai',
  'Abu Dhabi',
  'Sharjah',
  'Ajman',
  'Umm Al Quwain',
  'Ras Al Khaimah',
  'Fujairah',
];

export const VENUE_TYPES: { value: VenueType; label: string }[] = [
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'cafe', label: 'Café' },
  { value: 'bar', label: 'Bar' },
  { value: 'lounge', label: 'Lounge' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'food_court', label: 'Food Court' },
  { value: 'ghost_kitchen', label: 'Ghost Kitchen' },
  { value: 'other', label: 'Other' },
];

export const SERVICE_MODES: { value: ServiceMode; label: string; description: string }[] = [
  {
    value: 'waiter',
    label: 'Table Service',
    description: 'Staff take orders at the table',
  },
  {
    value: 'self_service',
    label: 'Self-Service / Kiosk',
    description: 'Guests order at a kiosk or via QR code',
  },
  {
    value: 'hybrid',
    label: 'Hybrid',
    description: 'Both table service and self-service',
  },
];

// ── Business Hours ──────────────────────────────────────────────────────────

export interface Shift {
  open: string; // HH:MM (24h)
  close: string; // HH:MM (24h)
}

export interface DaySchedule {
  is_open: boolean;
  shifts: Shift[];
}

export type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

export type BusinessHours = Record<DayOfWeek, DaySchedule>;

export interface TemporaryClosure {
  is_closed: boolean;
  reason?: string;
  reopen_at?: string; // ISO datetime
}

export const DAYS_OF_WEEK: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export const DEFAULT_BUSINESS_HOURS: BusinessHours = {
  monday:    { is_open: true, shifts: [{ open: '10:00', close: '23:00' }] },
  tuesday:   { is_open: true, shifts: [{ open: '10:00', close: '23:00' }] },
  wednesday: { is_open: true, shifts: [{ open: '10:00', close: '23:00' }] },
  thursday:  { is_open: true, shifts: [{ open: '10:00', close: '00:00' }] },
  friday:    { is_open: true, shifts: [{ open: '10:00', close: '12:00' }, { open: '13:30', close: '00:00' }] },
  saturday:  { is_open: true, shifts: [{ open: '10:00', close: '00:00' }] },
  sunday:    { is_open: true, shifts: [{ open: '10:00', close: '23:00' }] },
};

// UAE/Gulf timezones
export const COMMON_TIMEZONES = [
  { value: 'Asia/Dubai', label: 'Dubai (GMT+4)' },
  { value: 'Asia/Riyadh', label: 'Riyadh (GMT+3)' },
  { value: 'Asia/Qatar', label: 'Qatar (GMT+3)' },
  { value: 'Asia/Bahrain', label: 'Bahrain (GMT+3)' },
  { value: 'Asia/Kuwait', label: 'Kuwait (GMT+3)' },
  { value: 'Asia/Muscat', label: 'Muscat (GMT+4)' },
  { value: 'Europe/London', label: 'London (GMT+0/+1)' },
  { value: 'Europe/Paris', label: 'Paris (GMT+1/+2)' },
  { value: 'America/New_York', label: 'New York (GMT-5/-4)' },
  { value: 'Asia/Kolkata', label: 'India (GMT+5:30)' },
  { value: 'Asia/Shanghai', label: 'China (GMT+8)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (GMT+9)' },
];

/**
 * Get the current time in a specific timezone.
 * Returns { hours, minutes, dayOfWeek } in venue's local time.
 */
export function getVenueLocalTime(timezone: string): { hours: number; minutes: number; dayOfWeek: DayOfWeek } {
  const DAY_MAP: DayOfWeek[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit', minute: '2-digit', weekday: 'short',
      hour12: false,
    });
    const parts = formatter.formatToParts(new Date());
    const get = (type: string) => parts.find(p => p.type === type)?.value ?? '0';
    const hours = parseInt(get('hour'), 10);
    const minutes = parseInt(get('minute'), 10);
    // Map weekday abbreviation to index
    const weekday = get('weekday');
    const weekdayMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    const dayIndex = weekdayMap[weekday] ?? new Date().getDay();
    return { hours, minutes, dayOfWeek: DAY_MAP[dayIndex]! };
  } catch {
    // Fallback to UTC if timezone is invalid
    const now = new Date();
    return { hours: now.getUTCHours(), minutes: now.getUTCMinutes(), dayOfWeek: DAY_MAP[now.getUTCDay()]! };
  }
}

/**
 * Determine if a venue is currently open for ordering.
 * Priority: temporary_closure → business_hours → legacy open/close times.
 * Returns null if no hours are configured (assumed open).
 *
 * NOTE: `is_active` is NOT checked here. It means "deactivated by GatheredHQ platform"
 * (e.g., terms violation, unpaid bills) and is a separate concern from business hours.
 * Owner-initiated closures use `temporary_closure` only.
 */
export function isVenueCurrentlyOpen(params: {
  timezone?: string;
  businessHours?: BusinessHours | null;
  temporaryClosure?: TemporaryClosure | null;
  isActive?: boolean;  // kept for API compat but NOT used for open/close determination
  openTime?: string | null;
  closeTime?: string | null;
}): boolean | null {
  const { timezone = 'Asia/Dubai', businessHours, temporaryClosure, openTime, closeTime } = params;

  // 1. Temporary closure (owner-initiated via dashboard settings)
  // is_closed=true is authoritative — venue is closed until owner turns it off.
  // reopen_at is informational only (shown to guests as "Reopens at...").
  if (temporaryClosure?.is_closed) {
    return false;
  }

  // 3. Business hours (structured weekly schedule takes priority)
  if (businessHours) {
    const { hours, minutes, dayOfWeek } = getVenueLocalTime(timezone);
    const daySchedule = businessHours[dayOfWeek];

    if (!daySchedule || !daySchedule.is_open) return false;
    if (daySchedule.shifts.length === 0) return false;

    const currentMinutes = hours * 60 + minutes;

    for (const shift of daySchedule.shifts) {
      const [oh = 0, om = 0] = shift.open.split(':').map(Number);
      const [ch = 0, cm = 0] = shift.close.split(':').map(Number);
      const openM = oh * 60 + om;
      const closeM = ch * 60 + cm;

      if (closeM <= openM) {
        // Overnight shift (e.g. 22:00-02:00)
        if (currentMinutes >= openM || currentMinutes < closeM) return true;
      } else {
        if (currentMinutes >= openM && currentMinutes < closeM) return true;
      }
    }

    return false;
  }

  // 4. Legacy simple open/close times (fallback)
  if (!openTime || !closeTime) return null;

  const { hours, minutes } = getVenueLocalTime(timezone);
  const currentMinutes = hours * 60 + minutes;
  const [oh = 0, om = 0] = openTime.split(':').map(Number);
  const [ch = 0, cm = 0] = closeTime.split(':').map(Number);
  const openMinutes = oh * 60 + om;
  const closeMinutes = ch * 60 + cm;

  if (openMinutes === closeMinutes) return true; // 24h
  if (closeMinutes < openMinutes) return currentMinutes >= openMinutes || currentMinutes < closeMinutes;
  return currentMinutes >= openMinutes && currentMinutes < closeMinutes;
}

/**
 * Get the next opening time for display purposes.
 */
export function getNextOpeningInfo(params: {
  timezone?: string;
  businessHours?: BusinessHours | null;
  openTime?: string | null;
}): string | null {
  const { timezone = 'Asia/Dubai', businessHours, openTime } = params;

  if (businessHours) {
    const { dayOfWeek } = getVenueLocalTime(timezone);
    const dayIdx = DAYS_OF_WEEK.indexOf(dayOfWeek);
    // Check today and next 7 days
    for (let i = 0; i < 7; i++) {
      const checkDay = DAYS_OF_WEEK[(dayIdx + i) % 7]!;
      const schedule = businessHours[checkDay];
      if (schedule.is_open && schedule.shifts.length > 0) {
        const firstShift = schedule.shifts[0]!;
        if (i === 0) return `Today at ${firstShift.open}`;
        if (i === 1) return `Tomorrow at ${firstShift.open}`;
        return `${checkDay.charAt(0).toUpperCase() + checkDay.slice(1)} at ${firstShift.open}`;
      }
    }
    return null;
  }

  if (openTime) return `at ${openTime}`;
  return null;
}

// ── Cover media ─────────────────────────────────────────────────────────────
// The venue cover can be a single image/video OR a story-style sequence of
// multiple photos/videos played in the guest hero. When `cover_media` has 2+
// items the guest app plays them as a tappable, auto-advancing story banner;
// with 0–1 items it falls back to the legacy single cover_image_url/cover_video_url.

export type CoverMediaType = 'image' | 'video';

export interface CoverMediaItem {
  url: string;
  type: CoverMediaType;
}

/** Max media items allowed in a venue's cover story. */
export const MAX_COVER_MEDIA = 10;

// ── Restaurant ──────────────────────────────────────────────────────────────

export interface Restaurant {
  id: string;
  organization_id: string;
  name: string;
  slug: string;
  venue_type: VenueType;
  service_mode: ServiceMode;
  address: string | null;
  emirate: Emirate | null;
  phone: string | null;
  website: string | null;
  google_map_link: string | null;
  logo_url: string | null;
  cover_image_url: string | null;
  cover_video_url: string | null;
  /** Story-style ordered cover media. Empty/absent → use single cover_image/cover_video. */
  cover_media: CoverMediaItem[] | null;
  description: string | null;
  email: string | null;
  google_place_id: string | null;
  /** Cached Google rating (0.0–5.0), synced from the Places API. Null until first sync. */
  google_rating: number | null;
  /** Cached total number of Google reviews. */
  google_rating_count: number | null;
  /** Last Google Places fetch attempt (ISO). Drives the weekly refresh cadence. */
  google_rating_synced_at: string | null;
  instagram_url: string | null;
  facebook_url: string | null;
  youtube_url: string | null;
  twitter_url: string | null;
  currency: string;
  open_time: string | null;
  close_time: string | null;
  timezone: string;
  business_hours: BusinessHours | null;
  temporary_closure: TemporaryClosure | null;
  is_active: boolean;
  tips_enabled: boolean;
  tip_mode: 'percentage' | 'fixed' | null;
  tip_presets: number[] | null;
  pay_online_enabled: boolean;
  split_bill_enabled: boolean;
  split_bill_multi_device: boolean;
  /** Location-based prepaid ordering ("In-Room Dining"): one reusable QR, guest enters a location number, pays online first. */
  location_ordering_enabled?: boolean;
  /** Owner-chosen location noun ("Room", "Villa", "Sunbed"...) — frozen onto orders at order time. */
  location_ordering_label?: string;
  ai_order_assistant_enabled: boolean;
  sound_alerts_enabled?: boolean;
  guest_theme_mode?: GuestThemeMode;
  /** Show the live vibes bar on the guest home page. Absent ⇒ true (what venues render today). */
  /** Show the live vibes bar on the main QR (dine-in / takeaway). */
  live_vibes_enabled?: boolean;
  /** Show the live vibes bar when the guest arrived through a room-service QR. */
  live_vibes_room_service_enabled?: boolean;
  /** Show published What's New banners on the main QR. */
  whats_new_enabled?: boolean;
  /** Show published What's New banners through a room-service QR. */
  whats_new_room_service_enabled?: boolean;
  /** Menu widget layout on the guest home page. Absent ⇒ 'scroll'. */
  menu_layout?: 'scroll' | 'grid';
  /** E.164 number for the guest "chat with management" button. Null/empty hides it. */
  whatsapp_number?: string | null;
  /**
   * Printable QR codes this venue offers: any of 'venue', 'table'.
   *
   * The In-Room QR is NOT here — it follows `location_ordering_enabled`, which also
   * decides whether guests can order to a room at all. Read both through
   * `enabledQrTypes()`, the single accessor that combines them with the plan.
   */
  enabled_qr_types?: string[];
  enabled_languages: string[];
  enabled_currencies: string[];
  wifi_settings: { ssid: string; password: string } | null;
  floor_plan_image_url: string | null;
  stripe_account_id: string | null;
  stripe_onboarding_complete: boolean;
  stripe_payouts_enabled: boolean;
  created_at: string;
  updated_at: string;
  /**
   * Org-level tax registration, joined in from `tax_config` (one TRN per legal entity,
   * shared across a multi-venue org). Only populated by `findBySlug` — other Restaurant-
   * returning queries (list/membership) omit these, hence optional.
   */
  trn?: string | null;
  is_vat_registered?: boolean;
  tax_mode?: 'inclusive' | 'exclusive';
  default_tax_rate?: number;
}

export interface VenueMember {
  id: string;
  restaurant_id: string;
  user_id: string;
  role: import('./auth.js').VenueRole;
  created_at: string;
}

export type StaffRole = 'kitchen_staff' | 'bar_staff' | 'waiter' | 'cashier' | 'delivery_staff';

export type DeliveryAssignment = 'kitchen' | 'bar' | 'both';

export type GuestThemeMode = 'light_only' | 'dark_only' | 'both_light_default' | 'both_dark_default';

export type DashboardTheme = 'light' | 'dark' | 'system';

export interface VenueStaff {
  id: string;
  venue_id: string;
  staff_id: string;
  pin: string;
  name: string;
  role: StaffRole;
  email: string | null;
  phone: string | null;
  avatar_url: string | null;
  shift: 'morning' | 'afternoon' | 'evening' | 'night' | 'flexible';
  preparation_assignment: DeliveryAssignment | null;
  is_active: boolean;
  created_at: string;
}

/**
 * The branch something belongs to, when it is being shown at a DIFFERENT branch.
 *
 * Shared by promotions and What's New banners so the two cannot drift — they are the same
 * promise to the guest ("this is not here; here is where it is") and are rendered by the
 * same label.
 *
 * The address is the load-bearing field. A guest deciding whether to go needs to know
 * where, and a branch name they have never heard of does not tell them. It is nullable
 * only because a venue may not have filled its address in yet.
 */
export interface SharedFromVenue {
  venueName: string;
  venueSlug: string;
  venueAddress: string | null;
  /**
   * The other branch's own currency.
   *
   * Venues in one organization CAN differ here — the portfolio view refuses to sum across
   * currencies for exactly this reason. A booking priced in the branch you happen to be
   * standing in, for an event held somewhere that charges in something else, is a money
   * bug that nothing downstream would catch.
   */
  venueCurrency: string | null;
}
